import { ReactNode, useState } from 'react';

import {
    useGetContext,
    useGetCustomerState,
    useGetFormalOffer
} from '@api/lendingAPI/lapiHooks';
import { TFormalOffer } from '@helpers/FormalOffers/formalOffers.types';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { tracking } from '@iwoca/frontend-tracking-library';
import { getCurrentLocation } from '@iwRouter/HistoryProvider';
import { getQueryParameter } from '@iwRouter/LocationHelper';
import { FormattedMessage } from 'react-intl';

import styles from './FundingRequirementsView.module.css';
import * as IwDataDogLogger from '../../../iwLogger/IwDataDogLogger';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { useTrackPageView } from '../../../tracking/pageView';
import { getDrawdownType } from '../../drawDown/getDrawdownType';
import FallbackErrorModal from '../../modals/fallbackErrorModal/FallbackErrorModal';
import { getLoanDurationInMonths } from '../../myOffers/MyOffers.helpers';
import { useWriteOfferStepOnMount } from '../../myOffers/offerFlowMemory/useSaveOfferStep';
import { MainSectionWrapper } from '../../pageStructure/mainSectionWrapper';
import { MoneyNoDecimals } from '../../ui/moneyNoDecimals';
import { Tooltip } from '../../ui/tooltip';
import FundingHeader from '../fundingHeader/FundingHeader';
import FundingHeaderLoading from '../fundingHeader/FundingHeaderLoading';
import { FundingProgressIndicator } from '../fundingSteps/FundingProgessIndicator/FundingProgressIndicator';
import FundingSteps from '../fundingSteps/FundingSteps';

const FundingRequirementsView = () => {
    useTrackPageView({ name: 'Funding Requirements' });

    const stateKey = useStateKey();
    const location = getCurrentLocation();
    const { data: customerState, isLoading: loadingCustomerState } =
        useGetCustomerState(stateKey);
    const { data } = useGetContext();

    const isAccessUser = data?.logged_in_user_type === 'access_user';

    let drawdownAmount: string | number | undefined = getQueryParameter(
        location,
        'amount'
    );
    const sweep = getQueryParameter(location, 'sweep');

    const offerId: string | undefined = getQueryParameter(location, 'offerId');
    const returnPath: string | undefined = getQueryParameter(
        location,
        'returnPath'
    );

    useWriteOfferStepOnMount({
        offerId,
        step: 'funding_requirements',
        amount: drawdownAmount ? Number(drawdownAmount) : undefined,
        sweep: sweep ? Number(sweep) : undefined
    });

    const [hideBackButton, setHideBackButton] = useState(false);

    if (!drawdownAmount) {
        IwDataDogLogger.logError(
            'getQueryParameter drawdownAmount is undefined',
            {
                tags: {
                    reporter: 'funding_requirements_view'
                }
            }
        );
    }
    if (!offerId) {
        IwDataDogLogger.logError('getQueryParameter offerId is undefined', {
            tags: {
                reporter: 'funding_requirements_view'
            }
        });
    }

    const { formalOffer, loadingFormalOffer } = useGetFormalOffer(offerId);

    if (loadingFormalOffer || loadingCustomerState) {
        return (
            <MainSectionWrapper>
                <FundingHeaderLoading />
                <div className={styles.container}></div>
            </MainSectionWrapper>
        );
    }

    if (formalOffer && customerState?.application && drawdownAmount) {
        const isInterestOnlyExtension =
            getDrawdownType(formalOffer) === 'interest_only_extension';

        drawdownAmount = parseInt(drawdownAmount);

        tracking.ampli.drawdownStarted({
            amount: drawdownAmount,
            formalOfferId: offerId!
        });

        const loanDuration: number = getLoanDurationInMonths(
            formalOffer.duration!
        );

        const businessName =
            customerState.application.company!.registered_company_name;

        return (
            <>
                {getCountry() === 'UK' && !isAccessUser && (
                    <FundingProgressIndicator step={2} />
                )}

                <FundingHeader
                    header={
                        <HeaderMessage
                            formalOffer={formalOffer}
                            isAccessUser={isAccessUser}
                            drawdownAmount={
                                <span className={styles.headerBlue}>
                                    <MoneyNoDecimals
                                        value={
                                            getDrawdownType(formalOffer) ===
                                            'revolving_credit_facility_cashless_activation'
                                                ? formalOffer.max_amount!
                                                : drawdownAmount
                                        }
                                    />
                                </span>
                            }
                            loanDuration={
                                <span className={styles.headerBlue}>
                                    <FormattedMessage
                                        id="_.funding_requirements.duration"
                                        defaultMessage="{loanDuration} months"
                                        values={{
                                            loanDuration: loanDuration
                                        }}
                                    />
                                </span>
                            }
                            tooltip={
                                <span>
                                    <Tooltip
                                        className={styles.tooltip}
                                        message={
                                            <FormattedMessage
                                                id="_.funding_requirements.tooltip"
                                                defaultMessage="You can adjust your choice by clicking 'Go back' below."
                                            />
                                        }
                                        placement="auto"
                                    />
                                </span>
                            }
                        />
                    }
                    subHeader={
                        <SubHeaderMessage
                            formalOffer={formalOffer}
                            isAccessUser={isAccessUser}
                            businessName={<span>{businessName}</span>}
                        />
                    }
                    returnPath={returnPath}
                    hideBackButton={hideBackButton}
                />

                <MainSectionWrapper>
                    <div className={styles.container}>
                        <FundingSteps
                            formalOffer={formalOffer}
                            drawdownAmount={drawdownAmount}
                            sweep={sweep}
                            isInterestOnlyExtension={isInterestOnlyExtension}
                            setHideBackButton={setHideBackButton}
                        />
                    </div>
                </MainSectionWrapper>
            </>
        );
    }
    return <FallbackErrorModal />;
};

// eslint-disable-next-line import/no-default-export
export default FundingRequirementsView;

const HeaderMessage = ({
    formalOffer,
    isAccessUser,
    drawdownAmount,
    loanDuration,
    tooltip
}: {
    formalOffer: TFormalOffer;
    isAccessUser: boolean;
    drawdownAmount: ReactNode;
    loanDuration: ReactNode;
    tooltip: ReactNode;
}) => {
    if (isAccessUser) {
        return (
            <FormattedMessage
                id="_.funding_requirements.accessUserHeader"
                defaultMessage="You are guaranteeing a loan of {drawdownAmount} with repayments over {loanDuration}"
                values={{ drawdownAmount, loanDuration }}
            />
        );
    }

    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.funding_requirements.interestOnlyHeader"
                    defaultMessage="Your loan with repayments over {loanDuration}"
                    values={{ loanDuration }}
                />
            );
        case 'revolving_credit_facility_cashless_activation':
        case 'revolving_credit_facility_first_drawdown':
            return (
                <FormattedMessage
                    id="_.funding_requirements.rcf_first_drawdown_header"
                    defaultMessage="You have chosen the Flexi credit line with {drawdownAmount}"
                    values={{ drawdownAmount }}
                />
            );
        case 'revolving_credit_facility_drawdown':
            return (
                <FormattedMessage
                    id="_.funding_requirements.rcf_drawdown_header"
                    defaultMessage="You are withdrawing further {drawdownAmount}"
                    values={{ drawdownAmount }}
                />
            );
        case 'topup':
        case 'repayment_reduction':
        default:
            return (
                <FormattedMessage
                    id="_.funding_requirements.header"
                    defaultMessage="You're drawing down {drawdownAmount} with repayments over {loanDuration}{tooltip}"
                    values={{ drawdownAmount, loanDuration, tooltip }}
                />
            );
    }
};

const SubHeaderMessage = ({
    formalOffer,
    isAccessUser,
    businessName
}: {
    formalOffer: TFormalOffer;
    isAccessUser: boolean;
    businessName: ReactNode;
}) => {
    if (isAccessUser) {
        return (
            <FormattedMessage
                id="_.funding_requirements.accessUserSubHeader"
                defaultMessage="Before we can send funds to {businessName}, we need you to complete a few final steps."
                values={{ businessName }}
            />
        );
    }

    const drawdownType = getDrawdownType(formalOffer);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.funding_requirements.repayment_reduction_subheader"
                    defaultMessage="Before we can reduce your monthly repayments, you need to complete a few final steps."
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.funding_requirements.interestOnlySubHeader"
                    defaultMessage="Before we can extend your loan, you need to complete a few final steps"
                />
            );
        case 'revolving_credit_facility_cashless_activation':
        case 'revolving_credit_facility_first_drawdown':
            return (
                <FormattedMessage
                    id="_.funding_requirements.rcf_first_drawdown_subheader"
                    defaultMessage="To activate your credit line, we kindly ask you to complete the following final steps."
                />
            );
        case 'revolving_credit_facility_drawdown':
            return (
                <FormattedMessage
                    id="_.funding_requirements.rcf_drawdown_subheader"
                    defaultMessage="Before we transfer the money to your account, you need to complete a few final steps."
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.funding_requirements.subheader"
                    defaultMessage="Before we can send funds to your account, we need you to complete a few final steps."
                />
            );
    }
};
